import { useContext, useState, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import { AdminAuctionContext } from "../context/AdminAuctionContext";
import { Completions as CompletionsSvc } from "../../services/ActiveAuctionService";
import sortCompletions from "../../utilities/sortCompletions";
import CompletionSalesReportModal from "../components/CompletionSalesReportModal";
import PrintHeader from "../components/PrintHeader";
import ReactToPrint from "react-to-print";
import CompletionCard from "../components/CompletionCard";
import AuctionInactiveMessage from "../components/AuctionInactiveMessage";
import { useEvent } from "@harelpls/use-pusher";

export default function Completions() {
    const {
        currentAuctionId,
        currentAuctionTitle,
        currentAuctionDate,
        currentAuctionIsLive,
        auctionChannel,
    } = useContext(AdminAuctionContext);

    const queryClient = useQueryClient();
    const [salesReportData, setSalesReportData] = useState({});
    const [completionSalesReportModalOpen, setCompletionSalesReportModalOpen] =
        useState(false);
    const [completionsToShow, setCompletionsToShow] = useState(10);

    const { data: completionsData = [] } = useQuery(
        ["completions", currentAuctionId, completionsToShow],
        async () => {
            const completions = await CompletionsSvc({
                auction_id: currentAuctionId,
                per_page: completionsToShow,
            });
            return sortCompletions(completions.data);
        },
        { refetchOnWindowFocus: false }
    );

    useEvent(auctionChannel, "App\\Events\\CompletionsUpdated", () => {
        queryClient.invalidateQueries(["completions", currentAuctionId]);
    });

    return (
        <>
            <iframe id="iframePrintContents" title="Print"></iframe>

            {!currentAuctionIsLive && <AuctionInactiveMessage />}

            <div className="mr-4">
                <div className="mb-10">
                    <div className="float-left text-lg bold">
                        Top Completions
                    </div>
                    <div className="float-right">
                        <Button
                            className="pt-4 pb-4"
                            onClick={() => {
                                if (completionsData.length > 0) {
                                    setSalesReportData(completionsData[0]);
                                    setCompletionSalesReportModalOpen(true);
                                }
                            }}
                        >
                            Generate Sales Report
                        </Button>
                    </div>
                    <div className="float-right mr-5">
                        <select
                            className="mt-1"
                            value={completionsToShow}
                            onChange={(e) => {
                                setCompletionsToShow(e.target.value);
                            }}
                        >
                            <option>10</option>
                            <option>25</option>
                            <option>100</option>
                            <option value="999">All</option>
                        </select>
                    </div>
                    <div className="clear-both"></div>
                </div>

                {completionsData.map((completion, i) => (
                    <Completion
                        key={i}
                        completion={completion}
                        number={i + 1}
                    />
                ))}

                <CompletionSalesReportModal
                    open={completionSalesReportModalOpen}
                    salesReportData={salesReportData}
                    auctionName={currentAuctionTitle}
                    auctionDate={currentAuctionDate}
                    onCancel={() => setCompletionSalesReportModalOpen(false)}
                />
            </div>
        </>
    );
}

const Completion = ({ completion, number }) => {
    const cardRef = useRef();
    const { currentAuctionTitle } = useContext(AdminAuctionContext);
    return (
        <div
            className="print"
            id={`completion-${completion.completion_id}`}
            key={completion.completion_id}
        >
            <div className="mb-1 text-right">
                <ReactToPrint
                    trigger={() => (
                        <Icon
                            type="print"
                            width="24px"
                            className="float-right mr-2 cursor-pointer no-print"
                            color="brand-gray-500"
                        />
                    )}
                    content={() => cardRef.current}
                />
                <div className="clear-both"></div>
            </div>
            <div ref={cardRef} className="tableEl">
                <PrintHeader
                    auction={`${currentAuctionTitle} Auction`}
                    title={`Completion ${number}`}
                />
                <CompletionCard completion={completion} />
            </div>
        </div>
    );
};
